import React, { useMemo } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  IntrospectionFragmentMatcher,
  InMemoryCache,
  ApolloClient,
} from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import ReactDOM from 'react-dom';
import Amplify, { Storage } from 'aws-amplify';
import { Vars } from '@/utils';
import { useApolloLink } from '@/hooks';
import AmplifyAuthenticator from '@/components/Auth/AmplifyAuthenticator';
import { ISignInProps } from 'aws-amplify-react/lib-esm/Auth/SignIn';
import { QueryClient, QueryClientProvider } from 'react-query';
import App from './App';
import CacheBuster from './CacheBuster';
import introspectionQueryResultData from './fragmentTypes.json';
import './styles/index.scss';
import '@availity/yup';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const queryClient = new QueryClient();

Amplify.configure(Vars.awsmobile);

Storage.configure({
  AWSS3: {
    bucket: `reports.leadarena-${Vars.env}`,
    region: 'us-east-1',
  },
});

const cache = new InMemoryCache({
  fragmentMatcher,
  addTypename: false,
  dataIdFromObject: (o: any) => o?.id,
  cacheRedirects: {
    Query: {
      node: (_, args, { getCacheKey }) => {
        return getCacheKey({
          id: args.id,
        });
      },
      currentNetwork: (_, args, { getCacheKey }) => {
        return getCacheKey({
          id: args.id,
        });
      },
    },
  },
});

const AuthenticatedApp: React.FunctionComponent<ISignInProps> = ({
  authState,
}) => {
  const link = useApolloLink(authState);

  const client = useMemo(() => {
    if (!link) {
      return null;
    }

    const client = new ApolloClient({
      link,
      connectToDevTools: true,
      cache,
    });

    return client;
  }, [link]);

  if (!link) return null;

  return (
    <ApolloProvider client={client as any}>
      <Router>
        <App />
      </Router>
    </ApolloProvider>
  );
};

const render = async (): Promise<any> => {
  ReactDOM.render(
    <CacheBuster>
      <QueryClientProvider client={queryClient}>
        <AmplifyAuthenticator>
          <AuthenticatedApp />
        </AmplifyAuthenticator>
      </QueryClientProvider>
    </CacheBuster>,
    document.querySelector('#root')
  );
};

// Render once
render();
